import React from 'react';

import { story } from '../content';
import { Panel, Receipt, Meter, GapRow } from './bits';

/* Deliverable 01 — the Customer Story, told as a ledger: a narrow sticky rail
   naming each element on the left, the content on the right. Promoted from the
   design lab's winning sub-view; the other tellings (journey/json/headlines)
   were not carried over.

   Row order and what each row shows follow the 2026-08-04 review:
   Jobs (single sentences, no measures, no receipts) → Previous solution →
   Trigger → Evaluated (no receipts, names promoted) → Chosen Solution →
   Verdict (no receipts) → Where it breaks down (keeps its quotes) →
   Future goals. */

function NodeBody({ node }) {
  return (
    <>
      {node.headline && <p className="dp-node-headline">{node.headline}</p>}
      {node.note && node.id !== 'jobs' && <p className="dp-note">{node.note}</p>}

      {node.id === 'jobs' && (
        <>
          {node.items.map((job) => (
            <div key={job.sentence} className={`dp-job${job.highlight ? ' dp-job-hl' : ''}`}>
              <p className="dp-job-sentence">{job.sentence}</p>
              <GapRow importance={job.importance} satisfaction={job.satisfaction} />
            </div>
          ))}
          <p className="dp-payoff">{node.payoff}</p>
        </>
      )}

      {node.id === 'considered' &&
        node.items.map((alt, i) => (
          <div key={alt.name} className="dp-alt">
            <h5>
              <span className="dp-alt-n">Solution {i + 1}:</span>
              {alt.name}
            </h5>
            {alt.reasons.map((r) => (
              <div key={r.reason} className="dp-row">
                <span className="dp-row-text">{r.reason}</span>
                <Meter value={r.weight} tone="lo" />
              </div>
            ))}
          </div>
        ))}

      {node.id === 'verdict' && (
        <>
          <div className="dp-verdict">
            <span className="dp-big">{node.satisfaction}</span>
            <span className="dp-big-of">/ {node.satisfactionOf}</span>
            <span className="dp-big-label">satisfaction with the choice she made</span>
          </div>
          <p className="dp-note dp-note-alert">{node.annotation}</p>
          <div className="dp-split">
            <div>
              <h5>drawbacks</h5>
              {node.drawbacks.map((d) => (
                <div key={d.text} className="dp-row">
                  <span className="dp-row-text">{d.text}</span>
                  <Meter value={d.weight} tone="lo" />
                </div>
              ))}
            </div>
            <div>
              <h5>positives</h5>
              {node.positives.map((p) => (
                <div key={p.text} className="dp-row">
                  <span className="dp-row-text">{p.text}</span>
                  <Meter value={p.weight} tone="hi" />
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {node.id === 'breaks' &&
        node.items.map((item) => (
          <div key={item.situation} className="dp-row">
            <span className="dp-row-text">{item.situation}</span>
            <Receipt quote={item.quote} timestamp={item.timestamp} />
          </div>
        ))}

      {node.id === 'future' &&
        node.items.map((item) => (
          <p key={item.goal} className="dp-goal">
            {item.goal}
          </p>
        ))}
    </>
  );
}

export default function StorySection() {
  return (
    <section className="dp-section" id="research">
      <div className="dp-shell">
        <h2>{story.title}</h2>
        <p className="dp-body">{story.intro}</p>

        <Panel
          label={story.label}
          kicker={story.kicker}
          title={story.participant}
          meta={story.duration}
        >
          <div className="dp-hook">
            <span className="dp-hook-label">{story.hook.label}</span>
            <p>{story.hook.text}</p>
            <Receipt quote={story.hook.quote} timestamp={story.hook.timestamp} open />
          </div>

          <div className="dp-ledger">
            {story.journey.map((node) => (
              <div key={node.id} className="dp-ledger-row">
                <div className="dp-ledger-rail">
                  <span className="dp-ledger-step">{node.step}</span>
                  <span className="dp-ledger-caption">{node.caption}</span>
                </div>
                <div className="dp-ledger-content">
                  <NodeBody node={node} />
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </section>
  );
}
